import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import useConsultantClinicDays from '../hooks/useConsultantBookings';
import DaysToggle from '../components/DaysToggleConsultants';
import TimeslotToggle from '../components/TimeslotToggle';
import FlatList from '../components/FlatList';
import purifyObject from '../utils/purifyObject';
import CTAButton from '../components/CTAButton';
import InitialBookingDisclaimer from '../components/InitialBookingDisclaimer';
import ConsultantBookingError from '../components/ConsultantBookingError';

import { ReactComponent as PersonPlus } from '../static/img/person-plus.svg';
import { ReactComponent as Location } from '../static/img/location.svg';
import { ReactComponent as CalendarUnavailable } from '../static/img/calendar-unavailable.svg';

import './Consultant.sass';

export default () => {
  const { gmcCode } = useParams();
  const [isMonthView, setIsMonthView] = useState(true);

  const params = new URLSearchParams(window.location.search);
  const dateFromUrl = params.get('date');
  const [currentSelectedDate, setCurrentSelectedDate] = useState(
    dateFromUrl ? moment(dateFromUrl) : null,
  );

  const date = params.get('date');
  const slot = params.get('slot');
  const [selectedSlot, setSelectedSlotState] = useState(slot);

  const setSelectedSlot = newSlot => {
    setSelectedSlotState(newSlot);
  };

  // Use more days for month view
  const numberOfDays = isMonthView ? 42 : 9;

  const history = useHistory();
  const [selectedHospitals, setSelectedHospitals] = useState([]);

  const { loaded, selectedSlots, bookableSlots, clinicDays, slots, hospitals } =
    useConsultantClinicDays(
      gmcCode,
      numberOfDays,
      isMonthView,
      currentSelectedDate,
      selectedSlot,
      selectedHospitals,
    );

  const [hideTimeslots, setHideTimeSlots] = useState();
  const [checkState, setCheckState] = useState(false);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);

  const iframeURL = window.location.origin;
  let parentURL = 'https://www.nuffieldhealth.com';
  if (iframeURL.includes('-preprod')) {
    parentURL = 'https://www.preprod.nuffieldhealth.dev';
  } else if (iframeURL.includes('-uat')) {
    parentURL = 'https://www01.uat.nuffieldhealth.dev';
  } else if (iframeURL.includes('-qa')) {
    parentURL = 'https://www01.qa.nuffieldhealth.dev';
  }

  useEffect(() => {
    if (hasUserNavigated) return;

    if (!date || !clinicDays?.selectableDays?.length || !clinicDays?.actions?.next) return;

    const availableDates = clinicDays.selectableDays.map(day => day.item.date.format('YYYY-MM-DD'));

    if (availableDates.includes(date)) {
      const match = clinicDays.selectableDays.find(
        day => day.item.date.format('YYYY-MM-DD') === date,
      );
      match?.select();
    } else if (!selectedSlot) {
      clinicDays.actions.next();
    }
  }, [clinicDays?.selectableDays, clinicDays.actions, hasUserNavigated, selectedSlot, date]);

  // Auto-select the earliest available date when no URL parameters are provided
  useEffect(() => {
    if (!dateFromUrl && !currentSelectedDate && clinicDays?.selectableDays?.length > 0) {
      const earliestAvailableDay = clinicDays.selectableDays.find(day => !day.disabled);
      if (earliestAvailableDay) {
        setCurrentSelectedDate(earliestAvailableDay.item.date);
        earliestAvailableDay.select();
      }
    }
  }, [clinicDays?.selectableDays, dateFromUrl, currentSelectedDate]);

  useEffect(() => {
    function dataLayerEvent() {
      if (bookableSlots) {
        let specialtyName;
        for (let i = 0; i < slots.length; i++) {
          if (slots[i].item.timeslots.length !== 0) {
            specialtyName = slots[i].item.timeslots[0].item[1][0].specialty;
          }
        }
        let hospitalNames = hospitals[0][1].hospitalName;
        if (hospitals.length > 1) {
          for (let i = 1; i < hospitals.length; i++) {
            hospitalNames += ', ' + hospitals[i][1].hospitalName;
          }
        }
        window.parent.postMessage(
          {
            event_id: 'dataLayer',
            data: {
              event: 'consultantWidgetLoaded',
              consultant: {
                id: `${gmcCode}`,
                location: `${hospitalNames}`,
                specialty: `${specialtyName}`,
              },
            },
          },
          parentURL,
        );
      } else {
        window.parent.postMessage(
          {
            event_id: 'dataLayer',
            data: {
              event: 'noAppointments',
              consultant: {
                id: `${gmcCode}`,
              },
            },
          },
          parentURL,
        );
      }
    }
    if (loaded) {
      if (checkState) {
        dataLayerEvent();
      } else {
        // timeout is used here to allow the bookableSlots state to update based on the api response and to only fire one dataLayer event in this useEffect.
        setTimeout(() => {
          setCheckState(true);
        }, 1000);
      }
    }
  }, [loaded, bookableSlots, checkState, gmcCode, parentURL, hospitals, slots]);
  const timeSlotsForSelectedHospital = useMemo(() => {
    let filteredGroupedTimeslots = slots.map(timeslot => {
      for (let i = 0; i < timeslot.item.timeslots.length; i++) {
        const slotLocation = timeslot.item.timeslots[i].item[1][0].hospital.hospitalId;
        const selectedHospitalInTimeslot = selectedHospitals.some(location =>
          slotLocation.includes(location),
        );
        if (selectedHospitalInTimeslot) {
          timeslot.item.timeslots[i].filterValid = true;
        } else if (!selectedHospitalInTimeslot) {
          timeslot.item.timeslots[i].filterValid = false;
        }
      }
      return timeslot;
    });
    const filteredData = filteredGroupedTimeslots.map(obj => ({
      ...obj,
      item: {
        name: obj.item.name,
        timeslots: obj.item.timeslots.filter(timeslot => timeslot.filterValid === true),
      },
    }));
    return filteredData;
  }, [slots, selectedHospitals]);

  const errorState = useSelector(s => s.consultantBookingError);
  const showConsultantError = Boolean(errorState);

  return (
    <>
      <InitialBookingDisclaimer />
      <div className="m--consultant">
        <div className="pattern" />
        <div className="header">
          <h2 className="h2">Book consultant</h2>
        </div>
        {showConsultantError === true ? (
          <ConsultantBookingError />
        ) : loaded ? (
          <>
            {bookableSlots === true ? (
              <div>
                <div className="datepicker">
                  <DaysToggle
                    days={clinicDays.selectableDays}
                    selectedDay={clinicDays.selectedDay}
                    actions={clinicDays.actions}
                    showMonth
                    hospitals={hospitals}
                    selectedHospitals={selectedHospitals}
                    setSelectedHospitals={setSelectedHospitals}
                    setHideTimeSlots={setHideTimeSlots}
                    setHasUserNavigated={setHasUserNavigated}
                    isMonthView={isMonthView}
                    toggleCalendarView={() => {
                      if (isMonthView) {
                        window.parent.postMessage(
                          {
                            event_id: 'dataLayer',
                            data: {
                              event: 'weekViewClick',
                            },
                          },
                          parentURL,
                        );
                      } else {
                        window.parent.postMessage(
                          {
                            event_id: 'dataLayer',
                            data: {
                              event: 'monthViewClick',
                            },
                          },
                          parentURL,
                        );
                      }
                      setIsMonthView(!isMonthView);
                    }}
                    onDateSelect={date => {
                      const momentDate = moment(date);
                      setCurrentSelectedDate(momentDate);
                    }}
                    timeslotComponent={
                      !hideTimeslots ? (
                        <TimeslotToggle
                          timeslots={timeSlotsForSelectedHospital}
                          selectedSlot={selectedSlot}
                          onSelectSlot={setSelectedSlot}
                          renderEmpty={() => (
                            <p className="no-appointments">No appointments available</p>
                          )}
                        />
                      ) : null
                    }
                  />
                  {!isMonthView && !hideTimeslots && (
                    <TimeslotToggle
                      timeslots={timeSlotsForSelectedHospital}
                      selectedSlot={selectedSlot}
                      onSelectSlot={setSelectedSlot}
                      renderEmpty={() => (
                        <p className="no-appointments">No appointments available</p>
                      )}
                    />
                  )}
                </div>

                {selectedHospitals.length > 0 && (
                  <FlatList
                    data={selectedSlots}
                    renderItem={slot => {
                      const { slotId, slotTime, slotDuration, consultant, hospital, slotDate } =
                        slot;
                      const displayDate =
                        currentSelectedDate || (date ? moment(date) : moment(slotDate));
                      const mSlotYear = displayDate.format('YYYY');
                      const mSlotDay = displayDate.format('dddd');
                      const mSlotDate = displayDate.format('Do MMMM');
                      const mSlotTime = moment(slotTime, 'HH:mm');
                      const mSlotEndTime = mSlotTime.clone().add(slotDuration, 'minutes');
                      return (
                        <div key={slotId} className="card">
                          <p
                            className="time"
                            aria-label={`${mSlotTime.format('HH:mm')} to ${mSlotEndTime.format('HH:mm')}, on ${mSlotDay} ${mSlotDate} ${mSlotYear}`}
                          >
                            {mSlotTime.format('HH:mm')}
                            {' - '}
                            {mSlotEndTime.format('HH:mm')}
                            <span className="slotDate">
                              {mSlotDay}
                              <br></br>
                              {mSlotDate}
                            </span>
                          </p>
                          <div className="details">
                            <p className="product-title bold">Consultation</p>
                            <div className="detail">
                              <div className="person-icon__wrapper">
                                <PersonPlus />
                              </div>
                              <p>{consultant.name}</p>
                            </div>
                            <div className="detail location__wrapper">
                              <span className="location-icon__wrapper">
                                <Location />
                              </span>
                              <p>{hospital.hospitalName}</p>
                            </div>
                          </div>
                          <div className="cta__wrapper">
                            <div className="consultation-fee__wrapper">
                              <p>Initial consultation fee</p>
                              <p
                                className="consultation-price__label"
                                aria-label={`${consultant.price} pounds`}
                              >
                                £{consultant.price}
                              </p>
                            </div>
                            <CTAButton
                              action={{
                                name: 'Select',
                                call: async () => {
                                  window.parent.postMessage(
                                    {
                                      event_id: 'dataLayer',
                                      data: {
                                        event: 'consultantSelectBooking',
                                        consultant: {
                                          consultation_time: `${displayDate.format('YYYY-MM-DD')}, ${slotTime}`,
                                          id: `${gmcCode}`,
                                          location: `${slot.hospital.hospitalName}`,
                                          specialty: `${slot.specialty}`,
                                        },
                                      },
                                    },
                                    parentURL,
                                  );
                                  history.push(
                                    `/consultants/${gmcCode}/details/`,
                                    purifyObject({
                                      ...slot,
                                      slotDate: displayDate.format('YYYY-MM-DD'),
                                    }),
                                  );
                                },
                                type: 'BOOK',
                              }}
                            />
                          </div>
                        </div>
                      );
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="consultant-error mt-30">
                <span>
                  <CalendarUnavailable />
                </span>
                <div className="error-text">
                  <h3>Sorry, there are no appointments available</h3>
                  <p>
                    Please{' '}
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`${process.env.REACT_APP_CMS_PUBLIC_URL}consultants`}
                    >
                      search to find a different consultant
                    </a>
                    . If you can’t find a suitable appointment, call{' '}
                    <a href="tel:03001231995">0300 123 1995</a> and we’ll help with your booking.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </>
  );
};
